import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { 
  getReporteHorarios, 
  getSedesHorarios, 
  getEmpleadosHorarios,
  getNovedades,
  actualizarEstadoNovedad
} from '../services/horariosApi';
import { resolverAlcanceABAC } from '../utils/abacHorarios';

const PLANTA_OFICIAL = {
  'BALMORAL': { cupos: 28, prefijo: 'BAL', desglose: '1 Admin, 1 Subadmin, 1 Coord Fruver, 2 Coord Admin, 1 Coord Recibo, 2 Ctrl Pérdidas, 13 Cajeros, 7 Surtidores' },
  'GRAN COLOMBIA': { cupos: 27, prefijo: 'GCO', desglose: '1 Admin, 1 Subadmin, 1 Coord Fruver, 2 Coord Admin, 1 Coord Recibo, 2 Ctrl Pérdidas, 11 Cajeros, 8 Surtidores' },
  'PALMA': { cupos: 15, prefijo: 'PAL', desglose: '1 Admin, 1 Coord Fruver, 2 Coord Admin, 1 Coord Recibo, 7 Cajeros, 3 Surtidores' },
  'PAMPA': { cupos: 17, prefijo: 'PAM', desglose: '1 Admin, 1 Coord Fruver, 2 Coord Admin, 1 Coord Recibo, 1 Ctrl Pérdidas, 6 Cajeros, 5 Surtidores' },
  'SAN JORGE': { cupos: 32, prefijo: 'SJO', desglose: '1 Admin, 1 Subadmin, 1 Coord Fruver, 2 Coord Admin, 1 Coord Recibo, 1 Ctrl Pérdidas, 13 Cajeros, 12 Surtidores' },
  'SAN MATEO': { cupos: 17, prefijo: 'SMA', desglose: '1 Admin, 1 Coord Fruver, 2 Coord Admin, 1 Coord Recibo, 1 Ctrl Pérdidas, 7 Cajeros, 4 Surtidores' },
  'BODEGA GRANO': { cupos: 5, prefijo: 'BGR', desglose: '1 Admin, 4 Surtidores Polivalentes' },
  'LLANO LARGO': { cupos: 23, prefijo: 'LLA', desglose: '2 Op Medios, 1 Admin, 1 Subadmin, 1 Coord Fruver, 2 Coord Admin, 1 Coord Recibo, 2 Ctrl Pérdidas, 9 Cajeros, 6 Surtidores' },
  'PUNTO DE ACOPIO': { cupos: 12, prefijo: 'ACO', desglose: '1 Op Medios, 1 Dir Fruver, 1 Jefe Compras, 1 Coord Recibo, 5 Empaque/Vinipelado, 2 Surtidor, 1 Conductor' },
  'CENTRO DE ACOPIO': { cupos: 12, prefijo: 'ACO', desglose: '1 Op Medios, 1 Dir Fruver, 1 Jefe Compras, 1 Coord Recibo, 5 Empaque/Vinipelado, 2 Surtidor, 1 Conductor' }
};

function TarjetaNodoCanva({ emp, colorBorde, icono }) {
  return (
    <div className={`bg-white rounded-2xl p-3.5 shadow-md border-l-4 ${colorBorde} border-t border-r border-b border-gray-100 hover:shadow-lg transition-all flex items-center gap-3 min-w-[210px] max-w-[260px]`}>
      <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-lg shrink-0 font-black text-slate-700">
        {icono}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-black text-slate-900 truncate" title={emp.nombre_completo}>
          {emp.nombre_completo}
        </p>
        <p className="text-[10px] font-bold text-gray-500 truncate">
          {emp.cargo_nombre || emp.cargo_original || 'Operativo'}
        </p>
        <div className="mt-1 flex items-center gap-1.5">
          {emp.codigo_interno ? (
            <span className="px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 font-mono font-black text-[9px] border border-blue-200">
              {emp.codigo_interno}
            </span>
          ) : (
            <span className="px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 font-bold text-[9px] border border-amber-200">
              Sin Código
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ReportesHorarios({ usuario, onVolver }) {
  const [subVista, setSubVista] = useState('ORGANIGRAMA');
  const [mes, setMes] = useState(() => new Date().toISOString().slice(0, 7));
  const [sedeFiltro, setSedeFiltro] = useState('');
  const [sedes, setSedes] = useState([]);
  const [turnosReporte, setTurnosReporte] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [sedeOrganigrama, setSedeOrganigrama] = useState('');
  const [solicitudesExtras, setSolicitudesExtras] = useState([]);
  const [cargando, setCargando] = useState(false);

  const cargarBase = async () => {
    setCargando(true);
    try {
      const [resSedes, resEmp, resRep, resNov] = await Promise.all([
        getSedesHorarios().catch(() => ({ success: false, data: [] })),
        getEmpleadosHorarios().catch(() => ({ success: false, data: [] })),
        getReporteHorarios(mes, '').catch(() => ({ success: false, data: [] })),
        getNovedades().catch(() => ({ success: false, data: [] }))
      ]);
      if (resSedes && resSedes.success) setSedes(resSedes.data || []);
      if (resEmp && resEmp.success) setEmpleados(resEmp.data || []);
      if (resRep && resRep.success) setTurnosReporte(resRep.data || []);
      
      if (resNov && resNov.success) {
        const soloExtras = (resNov.data || []).filter(n => {
          const tipoTexto = String(n.tipo_novedad || '').toUpperCase();
          const cantidadHoras = Number(n.horas_extra || 0);
          return tipoTexto.includes('EXTRA') || cantidadHoras > 0;
        });
        setSolicitudesExtras(soloExtras);
      }
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarBase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mes]);

  const alcanceABAC = useMemo(() => resolverAlcanceABAC(usuario, sedes), [usuario, sedes]);

  useEffect(() => {
    if (alcanceABAC.sedesPermitidas.length > 0 && !sedeOrganigrama) {
      setSedeOrganigrama(String(alcanceABAC.sedesPermitidas[0].id));
      if (!alcanceABAC.esGlobal) {
        setSedeFiltro(String(alcanceABAC.sedesPermitidas[0].id));
      }
    }
  }, [alcanceABAC, sedeOrganigrama]);

  const handleFiltrarTurnos = async () => {
    setCargando(true);
    try {
      const idSedeEnviar = /^\d+$/.test(String(sedeFiltro)) ? sedeFiltro : '';
      const res = await getReporteHorarios(mes, idSedeEnviar);
      if (res && res.success) setTurnosReporte(res.data || []);
    } finally {
      setCargando(false);
    }
  };

  const handleDecisionHoraExtra = async (idSolicitud, nuevoEstado) => {
    try {
      const resp = await actualizarEstadoNovedad(idSolicitud, nuevoEstado, usuario?.nombre || 'Gerencia');
      if (resp && resp.success) {
        cargarBase(); 
      }
    } catch (error) {
      alert("Error al actualizar: " + error.message);
    }
  };

  // ============================================================================
  // EXPORTADOR EXCEL "SUPER TOP" BASADO EN LA PLANTILLA REAL DE OPERACIÓN
  // ============================================================================
  const exportarExcelPlantillaOficial = () => {
    if (turnosReporte.length === 0) return alert('No hay turnos para exportar en este mes.');

    const wb = XLSX.utils.book_new();
    const sedeObj = sedes.find(s => String(s.id) === String(sedeFiltro));
    const sedeStr = sedeObj ? sedeObj.nombre.toUpperCase() : 'CONSOLIDADO MÚLTIPLES SEDES';

    // 1. Agrupar turnos por Empleado
    const mapaEmpleados = new Map();
    turnosReporte.forEach(t => {
      if (!mapaEmpleados.has(t.id_empleado)) {
        mapaEmpleados.set(t.id_empleado, {
          nombre: t.nombre_completo,
          cargo: t.cargo_nombre || 'OPERATIVO',
          codigo: t.codigo_interno || 'N/A',
          turnosPorFecha: {}
        });
      }
      mapaEmpleados.get(t.id_empleado).turnosPorFecha[String(t.fecha).split('T')[0]] = t;
    });

    // 2. Determinar las Semanas Exactas del Mes (Lunes a Domingo)
    const [anio, mesNum] = mes.split('-').map(Number);
    const primerDiaMes = new Date(anio, mesNum - 1, 1);
    const ultimoDiaMes = new Date(anio, mesNum, 0);
    
    let actual = new Date(primerDiaMes);
    const offset = (actual.getDay() + 6) % 7; // Retroceder al Lunes
    actual.setDate(actual.getDate() - offset);

    const semanas = [];
    while (actual <= ultimoDiaMes || (semanas.length > 0 && semanas[semanas.length - 1].dias.length < 7)) {
      if (semanas.length === 0 || semanas[semanas.length - 1].dias.length === 7) {
        if (actual > ultimoDiaMes) break;
        semanas.push({ dias: [] });
      }
      semanas[semanas.length - 1].dias.push(new Date(actual));
      actual.setDate(actual.getDate() + 1);
    }

    const DIAS_EXCEL = ['DOMINGO', 'LUNES', 'MARTES', 'MIERCOLES', 'JUEVES', 'VIERNES', 'SABADO'];

    // 3. Crear una Hoja de Excel (Sheet) por cada Semana
    semanas.forEach((sem) => {
      const ini = String(sem.dias[0].getDate()).padStart(2, '0');
      const fin = String(sem.dias[6].getDate()).padStart(2, '0');
      const nombreHoja = `${ini} AL ${fin} ${MESES_CAPITAL[mesNum-1].substring(0,3).toUpperCase()}`;
      
      const aoa = []; // Array de Arrays
      const row0 = [sedeStr, '', ''];
      const row1 = ['', '', ''];
      const row2 = ['COLABORADOR', 'CÓDIGO / ID', 'CARGO'];

      // Construir Cabeceras (5 columnas por cada día)
      sem.dias.forEach(d => {
        const diaNombre = DIAS_EXCEL[d.getDay()];
        const num = String(d.getDate()).padStart(2, '0');
        row0.push(`${diaNombre} ${num}`, '', '', '', '');
        row1.push('Turno 1', '', 'Turno 2', '', 'Total Horas');
        row2.push('Inicio', 'Final', 'Inicio', 'Final', '');
      });
      row0.push('Total Horas Semanales');
      row1.push('');
      row2.push('');

      aoa.push(row0, row1, row2);

      // 4. Llenar la Data por Empleado (Ordenados Alfabéticamente)
      const empsArray = Array.from(mapaEmpleados.values()).sort((a,b) => a.nombre.localeCompare(b.nombre));

      empsArray.forEach(emp => {
        const rowData = [emp.nombre, emp.codigo, emp.cargo];
        let sumaSemana = 0;

        sem.dias.forEach(d => {
          const fStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
          const t = emp.turnosPorFecha[fStr];
          
          if (t) {
            const esPartido = Boolean(t.hora_inicio_2 && t.hora_fin_2);
            const esSoloTarde = !esPartido && Number(String(t.hora_inicio_1).split(':')[0]) >= 12;

            const t1i = esSoloTarde ? '' : (t.hora_inicio_1 ? String(t.hora_inicio_1).slice(0,5) : '');
            const t1f = esSoloTarde ? '' : (t.hora_fin_1 ? String(t.hora_fin_1).slice(0,5) : '');
            const t2i = esPartido ? (t.hora_inicio_2 ? String(t.hora_inicio_2).slice(0,5) : '') : (esSoloTarde ? (t.hora_inicio_1 ? String(t.hora_inicio_1).slice(0,5) : '') : '');
            const t2f = esPartido ? (t.hora_fin_2 ? String(t.hora_fin_2).slice(0,5) : '') : (esSoloTarde ? (t.hora_fin_1 ? String(t.hora_fin_1).slice(0,5) : '') : '');
            
            const totalDia = Number(t.horas_computables || 0);
            sumaSemana += totalDia;

            const h = Math.floor(totalDia);
            const m = Math.round((totalDia - h) * 60);
            rowData.push(t1i, t1f, t2i, t2f, `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`);
          } else {
            // Empleado descansa o ausente ese día
            rowData.push('DESC', '', '', '', '00:00');
          }
        });

        // Suma final de la semana
        const hS = Math.floor(sumaSemana);
        const mS = Math.round((sumaSemana - hS) * 60);
        rowData.push(`${String(hS).padStart(2,'0')}:${String(mS).padStart(2,'0')}`);
        aoa.push(rowData);
      });

      const ws = XLSX.utils.aoa_to_sheet(aoa);

      // 5. Aplicar Celdas Combinadas (Merges) replicando tu formato original
      const merges = [];
      sem.dias.forEach((d, i) => {
        const baseCol = 3 + (i * 5); 
        merges.push({ s: { r: 0, c: baseCol }, e: { r: 0, c: baseCol + 4 } }); // Día completo (Ej. LUNES 03)
        merges.push({ s: { r: 1, c: baseCol }, e: { r: 1, c: baseCol + 1 } }); // Turno 1
        merges.push({ s: { r: 1, c: baseCol + 2 }, e: { r: 1, c: baseCol + 3 } }); // Turno 2
      });
      merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 2 } }); // Título Sede
      ws['!merges'] = merges;

      XLSX.utils.book_append_sheet(wb, ws, nombreHoja);
    });

    XLSX.writeFile(wb, `PLANTILLA_HORARIOS_${sedeStr}_${mes}.xlsx`);
  };

  const datosOrganigrama = useMemo(() => {
    const sedeActual = alcanceABAC.sedesPermitidas.find(s => String(s.id) === String(sedeOrganigrama)) || alcanceABAC.sedesPermitidas[0];
    if (!sedeActual) return null;

    const nomSedeUpper = String(sedeActual.nombre || '').toUpperCase().trim();
    const metaPlanta = PLANTA_OFICIAL[nomSedeUpper] || { cupos: 25, prefijo: sedeActual.prefijo || 'SED', desglose: 'Estructura operativa de punto' };

    const personalSede = empleados.filter(e => {
      const est = String(e.estado || 'ACTIVO').toUpperCase();
      if (est !== 'ACTIVO') return false;
      if (e.sede_id) return String(e.sede_id) === String(sedeActual.id);
      return String(e.sede_original || '').toUpperCase().includes(nomSedeUpper);
    });

    const direccion = [];
    const coordinacion = [];
    const cajeros = [];
    const surtidoresYOtros = [];

    personalSede.forEach(emp => {
      const cargoTxt = String(emp.cargo_nombre || emp.cargo_original || '').toUpperCase();
      if (cargoTxt.includes('SUB') || (cargoTxt.includes('ADMIN') && !cargoTxt.includes('COORD'))) {
        direccion.push(emp);
      } else if (
        cargoTxt.includes('COORD') ||
        cargoTxt.includes('FRUVER') ||
        cargoTxt.includes('RECIBO') ||
        cargoTxt.includes('PERDIDA') ||
        cargoTxt.includes('MEDIO') ||
        cargoTxt.includes('JEFE') ||
        cargoTxt.includes('DIR')
      ) {
        coordinacion.push(emp);
      } else if (cargoTxt.includes('CAJ')) {
        cajeros.push(emp);
      } else {
        surtidoresYOtros.push(emp);
      }
    });

    return {
      sede: sedeActual,
      metaPlanta,
      totalActivos: personalSede.length,
      conCodigo: personalSede.filter(e => e.codigo_interno).length,
      direccion,
      coordinacion,
      cajeros,
      surtidoresYOtros
    };
  }, [alcanceABAC, sedeOrganigrama, empleados]);

  const extrasPendientesCount = solicitudesExtras.filter(s => s.estado_aprobacion === 'PENDIENTE').length;

  const MESES_CAPITAL = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

  return (
    <div className="animate-fade-in">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center border-b border-gray-200 pb-4 mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-black text-gray-800 flex items-center gap-2">📊 Reportes</h2>
          <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mt-1">
            Organigrama de Planta, Aprobación Gerencial de Horas Extras y Exportación
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setSubVista('ORGANIGRAMA')}
            className={`px-4 py-2.5 rounded-xl text-xs font-black transition cursor-pointer ${
              subVista === 'ORGANIGRAMA' ? 'bg-slate-900 text-white shadow-sm' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            🏛️ Organigrama por Sede
          </button>
          <button
            onClick={() => setSubVista('EXTRAS')}
            className={`px-4 py-2.5 rounded-xl text-xs font-black transition cursor-pointer flex items-center gap-1.5 ${
              subVista === 'EXTRAS' ? 'bg-amber-500 text-white shadow-sm' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            <span>⏱️ Horas Extras (Gerencia)</span>
            {extrasPendientesCount > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-red-600 text-white text-[10px]">
                {extrasPendientesCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setSubVista('TURNOS')}
            className={`px-4 py-2.5 rounded-xl text-xs font-black transition cursor-pointer ${
              subVista === 'TURNOS' ? 'bg-slate-900 text-white shadow-sm' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            📅 Exportación de Turnos (Excel)
          </button>
          <button
            onClick={onVolver}
            className="px-5 py-2.5 rounded-xl bg-white border border-gray-200 font-bold text-xs text-slate-700 hover:bg-gray-100 transition cursor-pointer"
          >
            Volver
          </button>
        </div>
      </div>

      {subVista === 'ORGANIGRAMA' && (
        <div className="space-y-6 animate-fade-in">
          <div className="bg-white/80 p-4 rounded-2xl border border-gray-100 shadow-sm flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap gap-2">
              {alcanceABAC.sedesPermitidas.map(s => (
                <button
                  key={s.id}
                  onClick={() => setSedeOrganigrama(String(s.id))}
                  className={`px-3.5 py-2 rounded-xl text-xs font-black transition cursor-pointer flex items-center gap-1.5 ${
                    String(sedeOrganigrama) === String(s.id)
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200'
                  }`}
                >
                  <span>{s.nombre}</span>
                  <span className="text-[10px] opacity-75 font-mono">({s.prefijo})</span>
                </button>
              ))}
            </div>
          </div>

          {datosOrganigrama && (
            <div className="bg-gradient-to-br from-slate-50 via-white to-blue-50/40 rounded-[32px] p-6 sm:p-10 border border-gray-200/80 shadow-inner relative overflow-x-auto">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white/90 p-5 rounded-3xl border border-gray-100 shadow-sm mb-10 gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 rounded-lg bg-blue-600 text-white font-mono font-black text-xs">
                      {datosOrganigrama.sede.prefijo || datosOrganigrama.metaPlanta.prefijo}
                    </span>
                    <h3 className="text-xl font-black text-slate-900">
                      PUNTO DE VENTA: {datosOrganigrama.sede.nombre}
                    </h3>
                  </div>
                  <p className="text-xs text-gray-500 font-medium mt-1">
                    <b>Planta Autorizada:</b> {datosOrganigrama.metaPlanta.desglose}
                  </p>
                </div>
                <div className="flex gap-3">
                  <div className="px-4 py-2 rounded-2xl bg-emerald-50 border border-emerald-200 text-center">
                    <span className="block text-[9px] font-black uppercase text-emerald-700">Planta Activa</span>
                    <span className="text-base font-black text-emerald-900">
                      {datosOrganigrama.totalActivos} / {datosOrganigrama.metaPlanta.cupos} cupos
                    </span>
                  </div>
                  <div className="px-4 py-2 rounded-2xl bg-blue-50 border border-blue-200 text-center">
                    <span className="block text-[9px] font-black uppercase text-blue-700">Con Código ID</span>
                    <span className="text-base font-black text-blue-900">
                      {datosOrganigrama.conCodigo} asignados
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-center space-y-6">
                <div className="flex flex-col items-center">
                  <div className="px-4 py-1 rounded-full bg-indigo-100 text-indigo-800 font-black text-[10px] uppercase tracking-wider mb-3 border border-indigo-200">
                    👑 Nivel 1 • Dirección y Administración de Sede ({datosOrganigrama.direccion.length})
                  </div>
                  <div className="flex flex-wrap justify-center gap-4">
                    {datosOrganigrama.direccion.map(emp => (
                      <TarjetaNodoCanva key={emp.id || emp.documento} emp={emp} colorBorde="border-l-indigo-600" icono="👔" />
                    ))}
                  </div>
                </div>

                <div className="w-0.5 h-8 bg-gradient-to-b from-indigo-300 to-purple-300"></div>

                <div className="flex flex-col items-center w-full">
                  <div className="px-4 py-1 rounded-full bg-purple-100 text-purple-800 font-black text-[10px] uppercase tracking-wider mb-3 border border-purple-200">
                    🛡️ Nivel 2 • Coordinación Operativa, Recibo, Fruver y Control ({datosOrganigrama.coordinacion.length})
                  </div>
                  <div className="flex flex-wrap justify-center gap-3.5 max-w-5xl">
                    {datosOrganigrama.coordinacion.map(emp => (
                      <TarjetaNodoCanva key={emp.id || emp.documento} emp={emp} colorBorde="border-l-purple-500" icono="📋" />
                    ))}
                  </div>
                </div>

                <div className="w-0.5 h-8 bg-gradient-to-b from-purple-300 to-emerald-400"></div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full pt-2">
                  <div className="bg-white/70 rounded-3xl p-5 border border-emerald-200/80 shadow-sm flex flex-col items-center">
                    <div className="px-4 py-1 rounded-full bg-emerald-100 text-emerald-800 font-black text-[10px] uppercase tracking-wider mb-4 border border-emerald-200">
                      💳 Nivel 3A • Línea de Cajas y Polivalentes ({datosOrganigrama.cajeros.length})
                    </div>
                    <div className="flex flex-wrap justify-center gap-3 w-full">
                      {datosOrganigrama.cajeros.map(emp => (
                        <TarjetaNodoCanva key={emp.id || emp.documento} emp={emp} colorBorde="border-l-emerald-500" icono="🛒" />
                      ))}
                    </div>
                  </div>

                  <div className="bg-white/70 rounded-3xl p-5 border border-amber-200/80 shadow-sm flex flex-col items-center">
                    <div className="px-4 py-1 rounded-full bg-amber-100 text-amber-800 font-black text-[10px] uppercase tracking-wider mb-4 border border-amber-200">
                      📦 Nivel 3B • Surtidores, Bodega y Apoyo ({datosOrganigrama.surtidoresYOtros.length})
                    </div>
                    <div className="flex flex-wrap justify-center gap-3 w-full">
                      {datosOrganigrama.surtidoresYOtros.map(emp => (
                        <TarjetaNodoCanva key={emp.id || emp.documento} emp={emp} colorBorde="border-l-amber-500" icono="📦" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {subVista === 'EXTRAS' && (
        <div className="space-y-4 animate-fade-in">
          <div className="bg-amber-50/80 p-4 rounded-2xl border border-amber-200 flex justify-between items-center">
            <div>
              <h3 className="text-sm font-black text-amber-950">⏱️ Control y Aprobación Gerencial de Horas Extras</h3>
              <p className="text-xs text-amber-800">
                Aquí llegan las solicitudes de horas extras creadas por los Administradores de Punto cuando un colaborador extiende su turno.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-gray-50 text-gray-500 uppercase font-black border-b">
                <tr>
                  <th className="p-4">Fecha</th>
                  <th className="p-4">Colaborador / Sede</th>
                  <th className="p-4">Horas Extra</th>
                  <th className="p-4">Motivo / Justificación</th>
                  <th className="p-4">Estado</th>
                  <th className="p-4 text-center">Decisión Gerencia</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {cargando ? (
                  <tr>
                    <td colSpan="6" className="p-10 text-center text-blue-600 font-bold animate-pulse">
                      Consultando base de datos...
                    </td>
                  </tr>
                ) : solicitudesExtras.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="p-10 text-center text-gray-400 font-bold">
                      No hay solicitudes de horas extras registradas.
                    </td>
                  </tr>
                ) : (
                  solicitudesExtras.map(sol => (
                    <tr key={sol.id} className="hover:bg-slate-50">
                      <td className="p-4 font-mono font-bold text-slate-700">{sol.fecha_inicio}</td>
                      <td className="p-4">
                        <span className="font-extrabold text-slate-900 block">{sol.nombre_completo}</span>
                        <span className="font-mono text-[10px] text-blue-600">{sol.codigo_interno || 'Sin código'} • {sol.sede_nombre}</span>
                      </td>
                      <td className="p-4 font-mono font-black text-sm text-purple-800">+{sol.horas_extra} hrs</td>
                      <td className="p-4 text-gray-600 font-medium">
                        {sol.motivo || 'Sin justificación'}
                        {sol.solicitado_por && (
                           <span className="block text-[9px] text-gray-400 mt-1">Sol: {sol.solicitado_por}</span>
                        )}
                      </td>
                      <td className="p-4">
                        <span className={`px-2.5 py-1 rounded-full font-black text-[10px] ${
                          sol.estado_aprobacion === 'APROBADA' ? 'bg-emerald-100 text-emerald-800' :
                          sol.estado_aprobacion === 'RECHAZADA' ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-800'
                        }`}>
                          {sol.estado_aprobacion || 'PENDIENTE'}
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        {(alcanceABAC.esGlobal || usuario?.cargo?.includes('GEREN')) && sol.estado_aprobacion === 'PENDIENTE' ? (
                          <div className="flex justify-center gap-2">
                            <button
                              onClick={() => handleDecisionHoraExtra(sol.id, 'APROBADA')}
                              className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-black text-[10px] cursor-pointer shadow-sm transition"
                            >
                              ✓ Aprobar
                            </button>
                            <button
                              onClick={() => handleDecisionHoraExtra(sol.id, 'RECHAZADA')}
                              className="px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white font-black text-[10px] cursor-pointer shadow-sm transition"
                            >
                              ✕ Rechazar
                            </button>
                          </div>
                        ) : sol.estado_aprobacion !== 'PENDIENTE' ? (
                          <span className="text-[10px] text-gray-500 font-bold block">
                            Auditado por: <br/>{sol.aprobado_por || 'Sistema'}
                          </span>
                        ) : (
                          <span className="text-[10px] text-gray-400 font-bold italic">Solo Gerencia</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {subVista === 'TURNOS' && (
        <div className="space-y-6 animate-fade-in">
          <div className="bg-white/80 p-5 rounded-2xl border border-gray-100 shadow-sm flex flex-col md:flex-row gap-4 items-end">
            <div className="w-full md:w-56">
              <label className="block text-[10px] font-black text-gray-500 uppercase mb-1">Mes a Consultar</label>
              <input
                type="month"
                value={mes}
                onChange={(e) => setMes(e.target.value)}
                className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-bold text-slate-800"
              />
            </div>

            <div className="w-full md:w-64">
              <label className="block text-[10px] font-black text-gray-500 uppercase mb-1">Sede para Exportar</label>
              <select
                value={sedeFiltro}
                onChange={(e) => setSedeFiltro(e.target.value)}
                disabled={!alcanceABAC.esGlobal && alcanceABAC.sedesPermitidas.length <= 1}
                className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-bold text-slate-800 bg-white"
              >
                {alcanceABAC.esGlobal && <option value="">Todas las sedes (Consolidado)</option>}
                {alcanceABAC.sedesPermitidas.map(s => (
                  <option key={s.id} value={s.id}>{s.nombre}</option>
                ))}
              </select>
            </div>

            <button
              onClick={handleFiltrarTurnos}
              disabled={cargando}
              className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-black text-xs cursor-pointer transition"
            >
              {cargando ? 'Consultando...' : 'Filtrar Datos'}
            </button>

            <button
              onClick={exportarExcelPlantillaOficial}
              disabled={turnosReporte.length === 0}
              className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl font-black text-xs cursor-pointer transition"
            >
              ⬇️ Descargar Formato Oficial (Excel)
            </button>
          </div>

          <div className="bg-blue-50/50 p-6 rounded-3xl border border-blue-100 text-center">
            <span className="text-4xl block mb-3">🗂️</span>
            <h3 className="text-lg font-black text-blue-900 mb-1">Generador de Plantillas Maestras</h3>
            <p className="text-xs text-blue-700/80 font-medium max-w-lg mx-auto">
              Al hacer clic en "Descargar Formato Oficial", el sistema agrupará los {turnosReporte.length} turnos encontrados en semanas exactas y generará un archivo de Excel con pestañas divididas, celdas combinadas y sumatorias idénticas al formato histórico de operación.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}